/**
 * Following a status after a screen hands its action to the IN-PLACE side chat (`useOpenSideChat`).
 *
 * A route push leaves the screen, so coming back remounts it and its reads run again; the phones get the
 * same for free, because their chat is a pushed screen and the Connectors screen reloads when it reappears.
 * The side chat opens OVER the screen instead, so nothing remounts: an Alfred row whose setup the chat just
 * finished kept reading "Connect" until a reload. Nothing pushes a connector change to the browser, so the
 * screen re-reads while the chat it handed off to stays open, and once more when that chat closes.
 *
 * Bounded, because each read is a whole connectors-status call: the follow ends the moment the status it
 * watches changes (the thing it was waiting for) or `SIDE_CHAT_FOLLOW_MAX_MS` after the hand-off, and the
 * hook skips interval reads in a hidden tab.
 *
 * A close PAUSES the follow rather than ending it: it reads once and waits, and a re-open resumes the
 * interval. The chat's open state is synced with the `?chat` URL parameter, so a hand-off can see the
 * chat close and re-open within a render or two; ending on the first close stopped the follow before
 * the setup it was waiting for had even started.
 *
 * Pure and import-free so it is unit-testable in the node-env suite; `use-side-chat-follow.ts` binds it.
 */

/** How often the followed status is re-read while the side chat stays open. */
export const SIDE_CHAT_FOLLOW_INTERVAL_MS = 10_000;

/** How long the interval reads run after the hand-off. A setup takes a few minutes; a chat left open for an hour should not keep asking. */
export const SIDE_CHAT_FOLLOW_MAX_MS = 5 * 60_000;

export type SideChatFollowInput = {
  armed: boolean;
  wasChatOpen: boolean;
  chatOpen: boolean;
  /** The watched status differs from what it was when the follow was armed. */
  settled: boolean;
  /** `SIDE_CHAT_FOLLOW_MAX_MS` has passed since the follow was armed. */
  expired: boolean;
};

export type SideChatFollowStep = {
  /** Re-read on the interval for as long as this step holds. */
  poll: boolean;
  /** Re-read once, now. */
  refreshNow: boolean;
  /** Whether the follow is still running after this step. */
  armed: boolean;
};

/**
 * One step of the follow, on every change to its input. An armed follow whose chat has not opened yet keeps
 * waiting rather than giving up: the hand-off and the chat's open land in the same render, but nothing
 * guarantees the order.
 */
export function sideChatFollowStep({ armed, wasChatOpen, chatOpen, settled, expired }: SideChatFollowInput): SideChatFollowStep {
  if (!armed || settled) return { poll: false, refreshNow: false, armed: false };
  if (chatOpen) return { poll: !expired, refreshNow: false, armed: true };
  if (wasChatOpen) return { poll: false, refreshNow: true, armed: !expired };
  return { poll: false, refreshNow: false, armed: !expired };
}
